import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ErrorState, MODEL_LABEL, PageHead, Skeleton, StatusPill, dateTime, money } from '@fa/ui';
import { ApiError, api, apiDownload } from '../lib/api.js';
import type { DocumentView } from '../lib/types.js';
import { useDocumentDrawer } from '../DocumentDrawer.js';
import { Inutilizacao } from '../Inutilizacao.js';

const STATUS_FILTERS = [
  { id: '', label: 'Todas' },
  { id: 'authorized', label: 'Autorizadas' },
  { id: 'rejected', label: 'Rejeitadas' },
  { id: 'contingency', label: 'Contingência' },
  { id: 'cancelled', label: 'Canceladas' },
] as const;

const MODEL_FILTERS = [
  { id: '', label: 'Todos os modelos' },
  { id: 'nfe', label: 'NF-e' },
  { id: 'nfce', label: 'NFC-e' },
  { id: 'nfse', label: 'NFS-e' },
] as const;

/**
 * Listagem de notas.
 *
 * A paginacao e por cursor (o proprio id, que e ULID e ordena por tempo). Com
 * `offset` a pagina 400 de um mes movimentado ficaria lenta e, pior, mostraria
 * documentos repetidos conforme novas notas entram no topo.
 */
export function Documents(): ReactNode {
  const { drawer, open } = useDocumentDrawer();
  const [aviso, setAviso] = useState<string | null>(null);
  const [inutilizando, setInutilizando] = useState(false);
  const [status, setStatus] = useState('');
  const [model, setModel] = useState('');
  const [search, setSearch] = useState('');
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const [page, setPage] = useState(0);

  const cursor = cursors[page] ?? null;

  const query = useQuery({
    queryKey: ['documents', status, model, search, cursor],
    queryFn: () => {
      const params = new URLSearchParams({ limit: '25' });
      if (status) params.set('status', status);
      if (model) params.set('model', model);
      if (search.trim()) params.set('reference', search.trim());
      if (cursor) params.set('cursor', cursor);
      return api<{ data: DocumentView[]; has_more: boolean; next_cursor: string | null }>(
        `/v1/documents?${params.toString()}`,
      );
    },
  });

  const reset = (fn: () => void): void => {
    fn();
    setCursors([null]);
    setPage(0);
  };

  return (
    <>
      <PageHead
        title="Notas"
        subtitle={query.data ? `${query.data.data.length} nota(s) nesta página` : 'Carregando...'}
        actions={
          <>
            <button className="btn btn-ghost" onClick={() => setInutilizando(true)}>
              Inutilizar numeração
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => {
                const params = new URLSearchParams();
                if (status) params.set('status', status);
                if (model) params.set('model', model);
                void apiDownload(`/v1/documents/export.csv?${params.toString()}`).catch((err: unknown) =>
                  setAviso(err instanceof ApiError ? err.message : 'Não foi possível gerar o CSV.'),
                );
              }}
            >
              Exportar CSV
            </button>
          </>
        }
      />

      {aviso && (
        <div className="alert info">
          <b>Resultado</b>
          {aviso}
          <div className="fix">
            <button className="btn btn-sm btn-ghost" onClick={() => setAviso(null)}>
              Fechar
            </button>
          </div>
        </div>
      )}

      {inutilizando && <Inutilizacao onClose={() => setInutilizando(false)} onDone={setAviso} />}

      <div className="card">
        <div className="filters">
          <input
            className="input"
            placeholder="Buscar pela referência do seu sistema"
            value={search}
            onChange={(e) => reset(() => setSearch(e.target.value))}
          />
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.id}
              className={`chip${status === f.id ? ' on' : ''}`}
              onClick={() => reset(() => setStatus(f.id))}
            >
              {f.label}
            </button>
          ))}
          <span style={{ flex: 1 }} />
          <select
            className="chip"
            style={{ minWidth: 150 }}
            value={model}
            onChange={(e) => reset(() => setModel(e.target.value))}
          >
            {MODEL_FILTERS.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </select>
        </div>

        {query.isLoading && <Skeleton rows={6} />}
        {query.isError && <ErrorState error={query.error} onRetry={() => void query.refetch()} />}

        {query.data && (
          <>
            <table>
              <thead>
                <tr>
                  <th>Nota</th>
                  <th>Modelo</th>
                  <th>Referência</th>
                  <th>Chave de acesso</th>
                  <th className="r">Valor</th>
                  <th>Situação</th>
                  <th className="r">Emitida</th>
                </tr>
              </thead>
              <tbody>
                {query.data.data.map((doc) => (
                  <tr key={doc.id} className="clickable" onClick={() => open(doc.id)}>
                    <td>
                      <b className="num">{doc.number.toLocaleString('pt-BR')}</b>
                      <span className="sub">Série {doc.serie}</span>
                    </td>
                    <td>{MODEL_LABEL[doc.model]}</td>
                    <td className="key">{doc.reference ?? '—'}</td>
                    <td className="key" style={{ fontSize: '.78rem', color: 'var(--ink-3)' }}>
                      {doc.access_key ? `…${doc.access_key.slice(-12)}` : '—'}
                    </td>
                    <td className="r num">{money(doc.totals?.total ?? null)}</td>
                    <td>
                      <StatusPill status={doc.status} />
                    </td>
                    <td className="r num">{dateTime(doc.timestamps.created_at)}</td>
                  </tr>
                ))}
                {query.data.data.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', color: 'var(--ink-3)', padding: 40 }}>
                      Nenhuma nota encontrada com esses filtros.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="pager">
              <span>Página {page + 1}</span>
              <span style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn btn-sm btn-ghost"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Anterior
                </button>
                <button
                  className="btn btn-sm btn-ghost"
                  disabled={!query.data.has_more}
                  onClick={() => {
                    setCursors((c) => {
                      const next = [...c];
                      next[page + 1] = query.data.next_cursor;
                      return next;
                    });
                    setPage((p) => p + 1);
                  }}
                >
                  Próxima
                </button>
              </span>
            </div>
          </>
        )}
      </div>

      {drawer}
    </>
  );
}
